import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { NoPartnerScreen } from "@/app/partner/components/NoPartnerScreen";
import { PartnerLandingPage } from "@/app/partner/pages/landing";
import { isFlexApp } from "@/hooks/useIsFlexApp";
import { allPartnersQueryOptions, userQueryOptionsSafe } from "@/lib/auth";

type PartnerSearchParams = {
	source?: string;
	action?: string;
};

export const Route = createFileRoute("/partner")({
	validateSearch: (search: Record<string, unknown>): PartnerSearchParams => {
		return {
			source: typeof search.source === "string" ? search.source : undefined,
			action: typeof search.action === "string" ? search.action : undefined,
		};
	},
	component: PartnerRoute,
});

function PartnerRoute() {
	const { source, action } = Route.useSearch();

	// Fetch user details to check if logged in (returns null if not authenticated)
	const { data: user, isLoading: isUserLoading } = useQuery({
		...userQueryOptionsSafe,
	});

	// Fetch partner data to check if user has partner access
	const { data: partnersResponse, isLoading: isPartnersLoading } = useQuery({
		...allPartnersQueryOptions,
		enabled: !!user, // Only fetch partners if user is logged in
		retry: false,
	});

	const isLoading = isUserLoading || (!!user && isPartnersLoading);

	// Build auth object
	const partners = partnersResponse?.result || [];
	const hasPartnerAccess = partners.length > 0;

	const auth = {
		status: !!user,
		data: user
			? {
					name: user.name,
					email: user.email,
					profile_image_url: user.profile_image_url,
				}
			: undefined,
		hasDashboardAccess: hasPartnerAccess,
	};

	// Show loading state briefly while checking auth
	if (isLoading) {
		return (
			<div className="min-h-screen bg-white flex items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#3F83F8]" />
			</div>
		);
	}

	// Flex app: a signed-in account with no partner lands here (redirected by the
	// root guard). Show the dedicated screen instead of the marketing landing.
	// Web keeps showing the landing page (with its "Logout" entry) unchanged.
	if (user && !hasPartnerAccess && isFlexApp()) {
		return <NoPartnerScreen email={user.email} />;
	}

	return (
		<PartnerLandingPage
			auth={auth}
			fromPlusPage={source === "plus"}
			action={action}
		/>
	);
}
